import React, { useState, useEffect, useRef } from "react";
import SectionHeader from "@src/components/widgets/SectionHeader/SectionHeader";
import { useParams } from "react-router-dom";
import * as ProductApi from "@src/api/ProductApi";
import {
  Checkbox,
  Button,
  PaginationControls,
  GridContainer,
  useToast,
} from "@swordsweeper/ssi-ui-component-library";
import SpinningLoader from "@src/components/common/SpinningLoader/SpinningLoader";
import { DistributorProduct } from "@src/types/product";
import PxProductTile from "@src/components/widgets/PxProductTile";
import { ProductRecommendationLabels } from "@src/components/widgets/ProductRecommendations/ProductRecommendationTypes";
import Menu, { MenuThemes } from "@src/components/common/Menu";
import NumberBubble from "@src/components/common/NumberBubble";
import map from "lodash/map";
import pick from "lodash/pick";
import Distributors, { DISTRIBUTOR_NAMES } from "@src/constants/Distributors";
import { useCurrentUserStore } from "@src/state/currentUserState";

const SORT_BY_ALPHABETICAL = "abc";
const SORT_BY_PRICE = "price";

type SortByOption = typeof SORT_BY_ALPHABETICAL | typeof SORT_BY_PRICE | null;

interface PaginationFilters {
  pageSize: number;
  page: number;
  totalPages: number;
  totalCount: number;
}

interface Filters {
  default?: boolean;
  [key: string]: any;
}

interface MenuController {
  setIsOpen: (isOpen: boolean) => void;
}

interface RouteParams extends Record<string, string | undefined> {
  type: string;
}

function ProductRecommendationPage() {
  const { showToast } = useToast();
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [products, setProducts] = useState<DistributorProduct[]>([]);
  const [paginationFilters, setPaginationFilters] = useState<PaginationFilters>(
    {
      pageSize: 20,
      page: 1,
      totalPages: 0,
      totalCount: 0,
    },
  );
  const [draftFilters, setDraftFilters] = useState<Filters>({
    distributors: [],
  });
  const [filters, setFilters] = useState<Filters>({ distributors: [] });
  const [sortBy, setSortBy] = useState<SortByOption>(null);
  const [activeFilterCount, setActiveFilterCount] = useState(0);
  const [filterMenuController, setFilterMenuController] =
    useState<MenuController | null>(null);
  const { type: productRecommendationType } = useParams<RouteParams>();
  const pageContentRef = useRef<HTMLDivElement>(null);
  const [isAnyProductAddingToCart, setIsAnyProductAddingToCart] =
    useState(false);
  const { isDistributorConnected } = useCurrentUserStore();

  useEffect(() => {
    handleLoadData();
  }, [
    productRecommendationType,
    sortBy,
    paginationFilters.pageSize,
    paginationFilters.page,
    filters.distributors.join(","), // Join distributor array to string for dependency comparison
  ]);

  const handleLoadData = async () => {
    setIsLoadingProducts(true);
    try {
      const combinedFilters = {
        ...pick(paginationFilters, ["page", "pageSize"]),
        ...filters,
        sortBy: sortBy,
      };
      const response = await ProductApi.getProductRecommendations(
        [productRecommendationType],
        combinedFilters,
      );
      const { results, ...updatedPaginationFilters } = response.body;
      setProducts(results);
      setPaginationFilters({
        ...paginationFilters,
        ...updatedPaginationFilters,
      });

      document.body.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      console.error(e);
      showToast({
        message: "Error loading products",
        variant: "error",
      });
    } finally {
      setIsLoadingProducts(false);
    }
  };

  const handleToggleDistributorFilter = (
    distributorSlug: string,
    checked: boolean,
  ) => {
    const currentDistributors: string[] = draftFilters.distributors || [];
    let updatedDistributors: string[];
    if (checked) {
      updatedDistributors = [...currentDistributors, distributorSlug];
    } else {
      updatedDistributors = currentDistributors.filter(
        (d) => d !== distributorSlug,
      );
    }
    setDraftFilters({
      ...draftFilters,
      distributors: updatedDistributors,
    });
  };

  const handleFilterChange = ({
    name,
    value,
  }: {
    name: string;
    value: any;
  }) => {
    setDraftFilters({
      ...draftFilters,
      [name]: value,
    });
  };

  const handleApplyFilters = () => {
    setActiveFilterCount(draftFilters.distributors.length);
    setFilters(draftFilters);
    filterMenuController?.setIsOpen(false);
  };

  const handlePaginationFilterChanged = ({
    name,
    value,
  }: {
    name: string;
    value: number;
  }) => {
    const updatedPaginationFilters = {
      ...paginationFilters,
      [name]: value,
    };
    if (name === "pageSize") {
      updatedPaginationFilters.page = 1;
    }
    setPaginationFilters(updatedPaginationFilters);
  };

  return (
    <div ref={pageContentRef} data-testid="product-recommendations-page-root">
      {isLoadingProducts && <SpinningLoader />}
      <SectionHeader
        header={
          <span
            className="text-xl"
            data-testid="product-recommendations-page-section-header"
          >
            Recommendations Based on Your{" "}
            {ProductRecommendationLabels[productRecommendationType]}
          </span>
        }
        rightSideContent={
          <div className="flex-row spaced-content">
            <Menu
              theme={MenuThemes.Large}
              buttonContent={
                <Button
                  className="btn btn-dropdown"
                  variant="pagination"
                  icon={
                    <i className="fa-regular fa-filter btn-dropdown-icon font-weight-normal" />
                  }
                  iconPosition="left"
                  onClick={() => {}}
                >
                  <div className="flex-row flex-start flex-center spaced-content">
                    <span>Filter</span>
                    {activeFilterCount > 0 && (
                      <NumberBubble value={activeFilterCount} />
                    )}
                  </div>
                </Button>
              }
              onReceiveController={setFilterMenuController}
            >
              <div className="padding-xxl">
                <div>
                  <div className="text-sm font-weight-bold margin-bottom-sm">
                    Distributors
                  </div>
                  <div className="flex-column">
                    {map(
                      Distributors,
                      (distributorSlug) =>
                        isDistributorConnected(distributorSlug) && (
                          <React.Fragment
                            key={`distributor-filter-${distributorSlug}`}
                          >
                            <Checkbox
                              className="checkbox text-sm"
                              label={DISTRIBUTOR_NAMES[distributorSlug]}
                              name={distributorSlug}
                              checked={
                                draftFilters.distributors.includes(
                                  distributorSlug,
                                ) || false
                              }
                              onChange={(checked: boolean) =>
                                handleToggleDistributorFilter(
                                  distributorSlug,
                                  checked,
                                )
                              }
                            />
                          </React.Fragment>
                        ),
                    )}
                  </div>
                </div>

                <div className="margin-top-lg">
                  <Button
                    className="btn btn-primary btn-md"
                    label="Apply Filters"
                    onClick={handleApplyFilters}
                  />
                </div>
              </div>
            </Menu>
          </div>
        }
      />
      <div className="padding-top-xxl">
        <GridContainer columns={{ base: 5 }} gap="var(--margin-sm)">
          {products?.map((product) => (
            <React.Fragment key={`product-recommendation-tile-${product.id}`}>
              <PxProductTile
                product={product}
                tileHeight="initial"
                onSetIsAddingToCart={setIsAnyProductAddingToCart}
                disableAddToCartButton={isAnyProductAddingToCart}
                className="width-100-important"
              />
            </React.Fragment>
          ))}
        </GridContainer>

        <div
          className="padding-top-xxl"
          data-testid="product-recommendations-page-pagination-controls"
        >
          <PaginationControls
            onChange={handlePaginationFilterChanged}
            values={paginationFilters}
          />
        </div>
      </div>
    </div>
  );
}

export default ProductRecommendationPage;
